import React, { useState } from 'react';
import {
  AppBar,
  Box,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Stack,
  Toolbar,
  Typography,
  useTheme,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';

import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { appPaths } from '../app-paths';
import { LogoWithText } from './logo-with-text';
import { MainNavbar } from './main-navbar';
import { useAuth } from '@dwelvio/auth-react';
import { IUserData } from '../types';
import { useNavigate } from 'react-router';
import { ConfirmationDialog } from './confirmation-dialog';
import { SelectedItemIcon } from './icons';
import { useAlert } from '@dwelvio/react-alert';

interface TopBarProps {
  onLogout: () => void;
  maxWidth?: number | string;
}

export function TopBar(props: TopBarProps) {
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>();
  const theme = useTheme();
  const { user, login, token } = useAuth<IUserData>();
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const navigate = useNavigate();
  const { show } = useAlert();

  const currentAccount = React.useMemo(
    () => user?.accounts.find((a) => a.id === user?.accountId),
    [user?.accountId, user?.accounts],
  );

  return (
    <AppBar
      position="relative"
      elevation={0}
      sx={(theme) => ({
        maxWidth: props.maxWidth ?? '100%',
        zIndex: theme.zIndex.drawer + 1,
        backgroundColor: { lg: theme.palette.background.default, xs: theme.palette.background.paper },
        justifySelf: 'anchor-center',
      })}
    >
      <Toolbar variant="dense">
        <IconButton
          edge="start"
          color="primary"
          onClick={() => setDrawerIsOpen(!drawerIsOpen)}
          sx={{ mr: 1, display: { lg: 'none' } }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ padding: 0, lineHeight: 0, textAlign: { sm: 'center', lg: 'left' } }}>
          <Link href={appPaths.dashboard()}>
            <LogoWithText height={35} color={theme.palette.primary.main} textColor={theme.palette.secondary.dark} />
          </Link>
        </Box>

        <Stack direction="row" spacing={2} sx={{ ml: 'auto', mr: 2, justifyContent: 'end', alignItems: 'center' }}>
          {(user?.accounts.length ?? 0) > 1 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              onClick={(event) => setAccountMenuAnchor(event.currentTarget)}
            >
              <HomeWorkIcon color="primary" />
              <Typography variant="body2" color="primary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                {currentAccount?.name}
              </Typography>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            onClick={(event) => setUserMenuAnchor(event.currentTarget)}
          >
            <AccountCircle color="primary" />
            <Typography variant="body2" color="primary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.username}
            </Typography>
          </Stack>
        </Stack>
      </Toolbar>
      <MainNavbar onDrawerClose={() => setDrawerIsOpen(false)} drawerIsOpen={drawerIsOpen} />
      <Popover
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            props.onLogout();
            setUserMenuAnchor(null);
          }}
        >
          Odhlásiť sa
        </MenuItem>
        <MenuItem onClick={() => user && navigate(appPaths.profile.navigate(user.id))}>Profil</MenuItem>
        <MenuItem onClick={() => navigate(appPaths.account.navigate())}>Účet</MenuItem>
      </Popover>
      <Popover
        open={Boolean(accountMenuAnchor)}
        anchorEl={accountMenuAnchor}
        onClose={() => setAccountMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <MenuList>
          {user?.accounts.map((account) => (
            <MenuItem
              key={account.id}
              onClick={() => setSelectedAccount(account.id)}
              selected={account.id === user?.accountId}
              disabled={account.id === user?.accountId}
            >
              <ListItemIcon>{currentAccount?.id == account.id && <SelectedItemIcon fontSize="small" />}</ListItemIcon>

              <ListItemText>{account.name}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
      <ConfirmationDialog
        open={Boolean(selectedAccount)}
        onCancel={() => {
          setAccountMenuAnchor(null);
          setSelectedAccount(undefined);
        }}
        onConfirm={() => {
          if (selectedAccount && user) {
            login({ username: user.username, token, accountId: selectedAccount })
              .then((u) => {
                if (!u) {
                  show.error('Nepodarilo sa prepnúť účet. Skúste sa prihlásiť znovu.');
                } else {
                  navigate(appPaths.unitList.navigate());
                }
              })
              .catch((err) => {
                show.error('Nepodarilo sa prepnúť účet. Skúste sa prihlásiť znovu.', err.message);
              });
          }
          setSelectedAccount(undefined);
        }}
        title="Prepnutie účtu"
      >
        <Typography variant="body1">
          Prepnúť účet na '{user?.accounts.find((a) => a.id === selectedAccount)?.name}' ?
        </Typography>
        <Typography variant="body2" color={theme.palette.text.secondary}>
          Zmení sa aktívny účet a všetky nastavenia budú zmenené na tento účet.
        </Typography>
      </ConfirmationDialog>
    </AppBar>
  );
}
